import React, { useCallback, useEffect, useState } from 'react';
import { formatDateTime } from '@/utils/displayDateTime';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, DataTable, ListPage, PageHeader, SegmentedControl, StatusBadge } from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { materialCountService, type CountSummary } from '../../../services/materialCountService';
import MaterialsSubnav from '../components/MaterialsSubnav';
import { countPath } from '../utils/siteDrillThrough';


type StatusFilter = 'OPEN' | 'CLOSED' | 'ALL';
type Load = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; counts: CountSummary[] };

const COLUMNS: DataTableColumn<CountSummary>[] = [
  {
    key: 'site',
    header: 'Site',
    mobile: 'title',
    className: 'tw-min-w-[14rem]',
    cell: (count) => (
      <span className="tw-font-medium tw-text-content">{count.site.number ? `${count.site.name} #${count.site.number}` : count.site.name}</span>
    ),
  },
  { key: 'method', header: 'Method', mobile: 'subtitle', cell: (count) => count.methodLabel },
  {
    key: 'countedAt',
    header: 'Taken',
    mobileLabel: 'Taken',
    className: 'tw-whitespace-nowrap',
    cell: (count) => formatDateTime(count.countedAt),
  },
  {
    key: 'progress',
    header: 'Bins',
    mobileLabel: 'Bins',
    className: 'tw-whitespace-nowrap tw-tabular-nums',
    cell: ({ lines }) => (
      <span>
        <span className="tw-text-content">{lines.posted}</span>
        <span className="tw-text-content-muted"> posted · {lines.counted} counted · {lines.total} total</span>
        {lines.investigating ? <span className="tw-font-medium tw-text-orange-700"> · {lines.investigating} investigating</span> : null}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    mobile: 'trailing',
    cell: (count) => (
      <StatusBadge status={count.status} variant={count.status === 'OPEN' ? 'info' : 'secondary'} label={count.status === 'OPEN' ? 'Open' : 'Closed'} soft pill />
    ),
  },
  { key: 'createdBy', header: 'Started By', mobileLabel: 'Started By', cell: (count) => count.createdBy ?? '–' },
];

/** Every count, open ones first by default. A count is started from its site. */
const CountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status: StatusFilter = params.get('status') === 'CLOSED' ? 'CLOSED' : params.get('status') === 'ALL' ? 'ALL' : 'OPEN';
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const refresh = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      setLoad({ status: 'ready', counts: await materialCountService.list(status === 'ALL' ? {} : { status }) });
    } catch (error) {
      setLoad({ status: 'error', message: error instanceof Error ? error.message : 'Failed to load counts' });
    }
  }, [status]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <ListPage>
      <PageHeader
        title="Material Locations"
        subtitle="Counts of a site's bins: drone fly-overs, dips and physical counts. Start one from the site."
        viewToggle={
          <SegmentedControl
            aria-label="Which counts"
            value={status}
            onChange={(value) => setParams(value === 'OPEN' ? {} : { status: value })}
            options={[
              { value: 'OPEN', label: 'Open' },
              { value: 'CLOSED', label: 'Closed' },
              { value: 'ALL', label: 'All' },
            ]}
          />
        }
        actions={[{ key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', onClick: () => void refresh() }]}
      />

      <MaterialsSubnav />

      <ListPage.Body>
        {load.status === 'error' ? (
          <Alert variant="danger">
            <p className="tw-mb-2">{load.message}</p>
            <Button size="sm" variant="outline-danger" onClick={() => void refresh()}>
              Try again
            </Button>
          </Alert>
        ) : (
          <DataTable
            fill
            columns={COLUMNS}
            data={load.status === 'ready' ? load.counts : []}
            rowKey={(count) => count.id}
            loading={load.status === 'loading'}
            onRowClick={(count) => navigate(countPath(count.id))}
            emptyMessage={
              status === 'OPEN' ? 'No count is open. Open a site on the Sites tab and choose "Start a count".' : 'No counts yet.'
            }
          />
        )}
      </ListPage.Body>
    </ListPage>
  );
};

export default CountsPage;
